/**
 * P7(a) A1 — <TeamInviteBanner>: the athlete-acceptance surface.
 *
 * Renders one card per claimed-pending team invite for the SIGNED-IN user
 * (self-contained via useMyTeamInvites; null when there are none / flag off /
 * signed out). Copy is the A1-ratified line: "{Coach name} invited you to
 * join {Team name}." with [Join team] [Decline]. Join → active membership;
 * Decline → the invite disappears + a 30-day re-invite cooldown server-side.
 * Mounted on Today (mobile + desktop + no-season) and Team.
 */

import { useState } from 'react';
import { useMyTeamInvites, type InviteActionError } from '../../hooks/useMyTeamInvites';
import { ReplanRefusalLine } from '../plan/ReplanRefusalLine';
import {
  GUARDIAN_HOLD_MESSAGE,
  AGE_BELOW_MINIMUM_MESSAGE,
  AGE_BAND_REQUIRED_MESSAGE,
  AGE_REVIEW_PENDING_MESSAGE,
} from '../../lib/planEdit';
import styles from './TeamInviteBanner.module.css';

export interface TeamInviteBannerProps {
  /** Fired after a successful Join — the host refreshes its team data
   *  (e.g. team.tsx refetches useTeamHub so the new team appears). */
  onJoined?: () => void;
  /**
   * B196 L9 (UX review finding 5 — two refusal lines never stack). Today's
   * no-season card can already be showing one of the four coded-refusal
   * sentences below the banner. When true, a refusal here that resolves to
   * one of those same four ratified lines is swallowed rather than printed
   * a second time directly above the identical sentence; a genuine
   * different failure (`GENERIC_INVITE_FAILURE`) still shows, because it
   * is not already on screen. Defaults to false, so every other mount
   * (Team, the happy-path Today hero) is byte-identical to before.
   */
  suppressHoldLine?: boolean;
}

// B202 — the generic-failure line for a refused accept/decline that is NOT
// the guardian hold. Reused verbatim from JoinTeamModal's fallback
// (components/team/JoinTeamModal.tsx:138, `err instanceof Error ? err.message
// : 'Something went wrong'`) — the nearest sibling "join a team" surface
// already ships this sentence; no new one is written here.
const GENERIC_INVITE_FAILURE = 'Something went wrong';

export function TeamInviteBanner({ onJoined, suppressHoldLine }: TeamInviteBannerProps) {
  const { invites, accept, decline } = useMyTeamInvites();
  const [busyId, setBusyId] = useState<string | null>(null);
  // B202 — per-invite refusal line. A held or otherwise-refused Accept/Decline
  // leaves the card in place with a reason instead of the button just
  // releasing (the empty catch this replaces swallowed the 403 entirely).
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (invites.length === 0) return null;

  const run = async (inviteId: string, action: (id: string) => Promise<void>, joined: boolean) => {
    if (busyId) return;
    setBusyId(inviteId);
    setErrors((prev) => {
      if (!(inviteId in prev)) return prev;
      const next = { ...prev };
      delete next[inviteId];
      return next;
    });
    try {
      await action(inviteId);
      if (joined) onJoined?.();
    } catch (err) {
      const code = (err as InviteActionError | undefined)?.code;
      // B196 L5 + L9 (F8) — accept/decline runs through `checkCoppaGate`
      // (spec §5 row 2, team invite accept + join), so a held or
      // unknown-age athlete can meet any of the four coded refusals here.
      const isCodedHold =
        code === 'guardian_consent_required' ||
        code === 'age_below_minimum' ||
        code === 'age_band_required' ||
        code === 'age_review_pending';
      // B196 L9 (UX review finding 5) — the card below already carries this
      // exact sentence; don't print it a second time above it.
      if (suppressHoldLine && isCodedHold) return;
      const message =
        code === 'guardian_consent_required'
          ? GUARDIAN_HOLD_MESSAGE
          : code === 'age_below_minimum'
            ? AGE_BELOW_MINIMUM_MESSAGE
            : code === 'age_band_required'
              ? AGE_BAND_REQUIRED_MESSAGE
              : code === 'age_review_pending'
                ? AGE_REVIEW_PENDING_MESSAGE
                : GENERIC_INVITE_FAILURE;
      setErrors((prev) => ({ ...prev, [inviteId]: message }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.stack}>
      {invites.map((inv) => (
        <div key={inv.id} className={styles.card} role="status">
          <p className={styles.line}>
            <strong>{inv.coachName}</strong> invited you to join{' '}
            <strong>{inv.teamName}</strong>.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.joinBtn}
              disabled={busyId === inv.id}
              aria-busy={busyId === inv.id || undefined}
              onClick={() => void run(inv.id, accept, true)}
            >
              Join team
            </button>
            <button
              type="button"
              className={styles.declineBtn}
              disabled={busyId === inv.id}
              onClick={() => void run(inv.id, decline, false)}
            >
              Decline
            </button>
          </div>
          {errors[inv.id] && (
            <div className={styles.holdLine}>
              <ReplanRefusalLine message={errors[inv.id]} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
